import Vue from 'vue'
import Vuex from 'vuex'
import mutations from './mutations'
import actions from './actions'
import createPersistedState from 'vuex-persistedstate'
import { SessionStatus, InputState, SessionResult, PopupCloseSetting, LabelTypes } from '@/constants'

Vue.use(Vuex)

const stateVariables = {
  status: {default: '', persist: true},
  token: {default: '', persist: true},
  campaign: {default: null, persist: true},
  session: {default: null, persist: true},
  popupDisplayState: {default: null, persist: true},
  messages: {default: [], persist: true},
  warnings: {default: [], persist: true},
  firstLogin: {default: false, persist: true},
  isLoading: {default: false, persist: true},
  isCPLoading: {default: false, persist: true},
  initialLoading: {default: true, persist: true},
  warning: {default: null, persist: true},
  error: {default: null, persist: true},
  selectedItems: {default: [], persist: true},
  selectedItemIndex: {default: 0, persist: true},
  statusItems: {default: [], persist: true},
  startIndex: {default: 0, persist: true},
  undoCount: {default: 0, persist: true},
  idleTimer: {default: null, persist: true},
  criticalError: {default: null, persist: true},
  lifelineCallback: {default: null, persist: true},
  faqsLoading: {default: false, persist: true},
  allFaqTopics: {default: [], persist: true},
  profilerEnabled: {default: false, persist: true},
  cprofilerEnabled: {default: false, persist: true},
  inputState: {default: InputState.Unknown, persist: true},
  currentLanguage: {default: 'en', persist: true},
  isLanguageReady: {default: false, persist: true},
  styleVariables: {default: {}, persist: true},
  styleLabels: {default: {}, persist: true},
  styleUrl: {default: null, persist: true},
  faviconUrl: {default: null, persist: true},
  votedFaqs: {default: {}, persist: true},
  selectedFaq: {default: null, persist: true},
  relatedFaqs: {default: [], persist: true},
  isIE: {default: false, persist: true},
  faqSearchResults: {default: [], persist: true},
  previouslySelectedFaqTopic: {default: null, persist: true},
  selectedFaqTopic: {default: null, persist: true},
  cart: {default: [], persist: true},
  shipping: {default: [], persist: true},
  tax: {default: null, persist: true},
  gateway: {default: null, persist: true},
  cartIsOpen: {default: false, persist: true},
  payment_sources: {default: [], persist: true},
  checkoutErrors: {default: null, persist: true},
  forceDisplayLoader: {default: false, persist: true},
  refreshSessionOnUndo: {default: false, persist: true},
  captchaLoaded: {default: false, persist: true},
  fulfillments: {default: [], persist: true},
  isMatchVerifyError: {default: [], persist: true},
  showChatbotHeader: {default: false, persist: true},
  websocket: {default: null, persist: false},
  pendingAction: {default: {resolve: null, reject: null, timeoutId: null}, persist: false},
  debug: {default: false, persist: true},
  subscriptionOnly: {default: false, persist: true},
  stepStack: {default: [], persist: true}, // step stack for tracking navigation history
  retentionAuthUrl: {default: null, persist: true},
  popupSession: {default: null, persist: true},
  isPopupLoading: {default: false, persist: true},
  isBannerHidden: {default: false, persist: true},
  isLabelHidden: {default: false, persist: true},
  isContentHidden: {default: false, persist: true},
  isMediaHidden: {default: false, persist: true}
}

let state = {}
let paths = []

for (const [key, value] of Object.entries(stateVariables)) {
  state[key] = value.default
  if (value.persist) {
    paths.push(key)
  }
}

function getPersistKey() {
  const params = new URLSearchParams(window.location.search)
  const isChatbot = params.get('is_chatbot_embed')
  if (isChatbot) {
    return 'ivrpath-chatbot'
  }
  return 'ivrpath'
}

export default new Vuex.Store({
  plugins: [createPersistedState({
    key: getPersistKey(),
    paths: paths
  })],
  state: state,
  getters: {
    isSessionStarted: function(state) {
      return !!state.token
    },
    isError: function(state) {
      return state.status === 'error'
    },
    isSessionValid: function(state, getters) {
      return getters.isSessionStarted && !!state.session
    },
    isSessionComplete: function(state) {
      return state.session && state.session.result === SessionResult.Complete
    },
    mustLogoutSession: function(state) {
      return state.session &&
        ([SessionResult.LifelineNoVerify, SessionResult.LifelineLocked].indexOf(state.session.result) !== -1)
    },
    isSessionActive: function(state, getters) {
      return getters.isSessionValid && state.session.is_active
    },
    isSessionActiveAndReady: function(state, getters) {
      return getters.isSessionActive && !state.isLoading
    },
    canLogoutSession: function(state, getters) {
      return getters.isSessionValid &&
        state.session.result === SessionResult.Active && state.session.status !== SessionStatus.Unmatched
    },
    isSessionUserKnown: function(state, getters) {
      return getters.isSessionValid && state.session.customer && state.session.is_verified && state.session.is_active
    },
    isLastItem: function(state) {
      return state.selectedItems.length &&
        (state.selectedItemIndex === state.selectedItems.length - 1)
    },
    currentSelectedItem: function(state) {
      if (state.selectedItems.length &&
        (state.selectedItemIndex >= 0) &&
        (state.selectedItemIndex < state.selectedItems.length)) {
        return state.selectedItems[state.selectedItemIndex]
      }

      return null
    },
    canGotoPreviousItem: function(state) {
      return state.selectedItemIndex > state.startIndex
    },
    isMainMenu: function(state, getters) {
      return getters.isSessionActiveAndReady && state.session.is_first_step &&
        ((state.inputState === InputState.Unknown) || (state.inputState === InputState.OkToSubmit))
    },
    processingGIF: function(state) {
      if (state.campaign && state.campaign.processing_gif) {
        return state.campaign.processing_gif.file
      }

      return require('@/assets/images/default_processing_gif.gif')
    },
    isLifelineActive: function(state) {
      return state.session && state.session.is_lifeline_active && !state.session.show_lifeline_contact
    },
    showContactForm: function(state, getters) {
      return !!state.criticalError || (getters.isSessionActive && state.session.show_lifeline_contact)
    },
    checkoutDisabled: function(state) {
      return !state.session || !state.session.has_checkout || process.env.VUE_APP_CHECKOUT_DISABLED === 'true'
    },
    isAlreadySessionUrl: function() {
      return window.location.pathname === '/session'
    },
    popupCloseCanUndo: function(state) {
      return state.popupSession && state.popupSession.can_undo &&
        ([
          PopupCloseSetting.Undo, PopupCloseSetting.UndoOrFirstInput, PopupCloseSetting.UndoOrLastInput
        ].indexOf(state.popupSession.popup_close_setting) !== -1)
    },
    popupCloseCanSelectFirstInput: function(state) {
      return state.popupSession && state.popupSession.valid_inputs && state.popupSession.valid_inputs.length &&
        ([
          PopupCloseSetting.FirstInput, PopupCloseSetting.UndoOrFirstInput
        ].indexOf(state.popupSession.popup_close_setting) !== -1)
    },
    popupCloseCanSelectLastInput: function(state) {
      return state.popupSession && state.popupSession.valid_inputs && state.popupSession.valid_inputs.length &&
        ([
          PopupCloseSetting.LastInput, PopupCloseSetting.UndoOrLastInput
        ].indexOf(state.popupSession.popup_close_setting) !== -1)
    },
    subscriptionStatusLabels: function(state) {
      return (state.styleLabels || {})[LabelTypes.SubscriptionStatus] || {}
    },
    activeSession: function(state) {
      return (state.session && state.session.is_popup && state.popupSession) ? state.popupSession : state.session
    }
  },
  mutations,
  actions
})
